import axios from 'axios';
import { ERROR_MESSAGES } from '../../shared/Constants';
import {
  applyAuthTokensFromResponse,
  authHeaderValue,
  getRefreshToken,
} from '../../../utils/authTokenStore';
import { clearAllClientAuthState } from '../../../utils/sessionCleanup';
import { getOrCreateDeviceId } from '../../../utils/deviceId';

const BASE_API_URL = process.env.REACT_APP_API_URL || '';

/** Same-origin in dev (CRA proxy) so HttpOnly cookies work; full URL in production. */
export const API_BASE_URL = BASE_API_URL;

const apiClient = axios.create({
  baseURL: BASE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  paramsSerializer: (params) => {
    const parts = [];
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && String(v).length > 0) {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
          }
        });
        return;
      }
      if (String(value).length > 0) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });
    return parts.join('&');
  },
});

apiClient.interceptors.request.use((config) => {
  const deviceId = getOrCreateDeviceId();
  if (deviceId) {
    config.headers = config.headers || {};
    config.headers['X-Device-Id'] = deviceId;
  }
  const bearer = authHeaderValue();
  if (bearer) {
    config.headers = config.headers || {};
    config.headers.Authorization = bearer;
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

function isAuthEndpoint(url) {
  if (!url) return false;
  return url.includes('/auth/login')
    || url.includes('/auth/signup')
    || url.includes('/auth/google')
    || url.includes('/auth/mfa/verify')
    || url.includes('/auth/refresh')
    || url.includes('/auth/logout');
}

async function runRefresh() {
  const refresh = getRefreshToken();
  const deviceId = getOrCreateDeviceId();
  const refreshResponse = await axios.post(
    `${BASE_API_URL}/auth/refresh`,
    refresh ? { refreshToken: refresh, deviceId } : { deviceId },
    {
      withCredentials: true,
      headers: deviceId ? { 'X-Device-Id': deviceId } : {},
    }
  );
  applyAuthTokensFromResponse(refreshResponse.data);
  return refreshResponse;
}

apiClient.interceptors.response.use(
  (response) => {
    const url = response.config?.url || '';
    if (url.includes('/auth/login') && response.data?.mfaRequired) {
      return response;
    }
    if (url.includes('/auth/')) {
      applyAuthTokensFromResponse(response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      !originalRequest
      || isAuthEndpoint(originalRequest.url)
      || originalRequest._skipAuthRefresh
    ) {
      return Promise.reject(error);
    }

    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, originalRequest });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await runRefresh();
        failedQueue.forEach((req) => req.resolve(apiClient(req.originalRequest)));
        failedQueue = [];
        return apiClient(originalRequest);
      } catch (refreshError) {
        failedQueue.forEach((req) => req.reject(refreshError));
        failedQueue = [];

        const sessionProbe = Boolean(originalRequest._authSessionProbe);
        if (!sessionProbe) {
          try {
            await authService.logout();
          } catch {
            /* session already gone */
          }
          clearAllClientAuthState();
        }

        return Promise.reject(
          sessionProbe
            ? refreshError
            : new Error(ERROR_MESSAGES.INVALID_REFRESH_TOKEN)
        );
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

export const authService = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  googleLogin: (payload) => apiClient.post('/auth/google', payload),
  oauthStatus: () => apiClient.get('/auth/oauth/status'),
  getUserCount: () => apiClient.get('/auth/user-count'),
  verifyMfa: (payload) => apiClient.post('/auth/mfa/verify', payload),
  signUp: (credentials) => apiClient.post('/auth/signup', credentials),
  refresh: () => runRefresh(),
  getUser: (config = {}) => apiClient.get('/users/me', config),
  logout: () => {
    const deviceId = getOrCreateDeviceId();
    return axios.post(`${BASE_API_URL}/auth/logout`, {}, {
      withCredentials: true,
      headers: deviceId ? { 'X-Device-Id': deviceId } : {},
    });
  },
  clearSession: (navigate) => {
    clearAllClientAuthState();
    if (typeof navigate === 'function') {
      navigate('/login');
    }
  },
};

export const adminService = {
  getTestUserConfig: () => apiClient.get('/api/admin/test-user/config'),
  provisionTestUser: (reset = true) =>
    apiClient.post('/api/admin/test-user', { reset }),
};

export const billingService = {
  getConfig: () => apiClient.get('/api/billing/config'),
  createCheckout: (plan, interval) =>
    apiClient.post('/api/billing/checkout-session', { plan, interval }),
  openPortal: () => apiClient.post('/api/billing/portal'),
};

export const mfaService = {
  status: () => apiClient.get('/api/users/me/mfa/status'),
  setup: () => apiClient.post('/api/users/me/mfa/setup'),
  enable: (code) => apiClient.post('/api/users/me/mfa/enable', { code }),
  stepUp: (code) => apiClient.post('/api/users/me/mfa/step-up', { code }),
  disable: (currentPassword, code) =>
    apiClient.post('/api/users/me/mfa/disable', { currentPassword, code }),
};
